import { create } from 'zustand';
import { container } from '../../generic/ioc/container.ts';
import INJECTION_TOKENS from '../../generic/ioc/injection-tokens.ts';
import { SNACKBAR_DEFAULT_VALUE, SnackbarProps } from '../../model/snackbar-props.ts';
import { convertToFloodlightPriorities, giveStartDateFromPeriod } from '../model/algorithm.ts';
import {
  ConversionWeightFormValues,
  ConversionWeightValues,
  DomainScoringValues,
} from '../model/bid-form.ts';
import { CONVERSION_WEIGHT_FORM_DEFAULT_VALUES, WEIGHT_PERIOD } from '../model/constant.ts';
import { Floodlight, FloodlightPlatform, FloodlightValue } from '../model/floodlight.ts';
import { BiddingScriptGenerationData } from '../model/script.ts';
import { Dv360AlgorithmPort } from '../port/dv360-algorithm.port.ts';

type FloodlightsPerPlatform = {
  cm360: FloodlightValue[];
  dv360: FloodlightValue[];
};

type ScriptState = {
  snackBarProps: SnackbarProps;
  previewScript: string;
  isGenerationLoading: boolean;
  isScriptPreview: boolean;
  floodlightAutoSelectValues: FloodlightValue[];
  floodlightsPerPlatform: FloodlightsPerPlatform;
  floodlightIdsValues: string[];
  isFloodlightPriorityActive: boolean;
  formDefaultValues: ConversionWeightFormValues;
  selectedPlatform: FloodlightPlatform;
};

type ScriptActions = {
  reset: () => void;
  showScriptPreview: () => void;
  hideScriptPreview: () => void;
  generateScript: (
    clientId: string,
    insertionOrderIds: string[],
    period: string,
    conversionWeightValues: ConversionWeightValues,
    domainScoringValues: DomainScoringValues,
    platform: FloodlightPlatform
  ) => Promise<boolean>;
  setFloodlightsPerPlatform: (
    cm360Floodlights: Floodlight[],
    dv360Floodlights: Floodlight[]
  ) => void;
  setFloodlightAutoSelectValues: (floodlights: Floodlight[]) => void;
  setFloodlightIdsValues: (value: string[]) => void;
  setFormDefaultValues: (values: ConversionWeightFormValues) => void;
  resetValues: () => void;
  setSelectedPlatform: (platform: FloodlightPlatform) => void;
};

const initialState: ScriptState = {
  snackBarProps: SNACKBAR_DEFAULT_VALUE,
  previewScript: '',
  isGenerationLoading: false,
  isScriptPreview: false,
  floodlightAutoSelectValues: [],
  floodlightsPerPlatform: { cm360: [], dv360: [] },
  floodlightIdsValues: [],
  isFloodlightPriorityActive: false,
  formDefaultValues: CONVERSION_WEIGHT_FORM_DEFAULT_VALUES,
  selectedPlatform: 'CM360',
};

// ADAPTERS DRIVEN
const algorithmRepository = container.get<Dv360AlgorithmPort>(
  INJECTION_TOKENS.DV360_ALGORITHM_REPOSITORY
);

export const useScriptStore = create<ScriptState & ScriptActions>((set) => ({
  ...initialState,
  reset: () => {
    set(initialState);
  },
  showScriptPreview: () => set({ isScriptPreview: true }),
  hideScriptPreview: () => set({ isScriptPreview: false }),
  generateScript: async (
    clientId: string,
    insertionOrderIds: string[],
    period: string,
    conversionWeightValues: ConversionWeightValues,
    domainScoringValues: DomainScoringValues,
    platform: FloodlightPlatform
  ): Promise<boolean> => {
    let generationInSuccess = true;
    set({ isGenerationLoading: true });
    try {
      const endDate = new Date();
      const startDate = giveStartDateFromPeriod(period ?? WEIGHT_PERIOD.PERIOD_90_DAYS);

      const priorities = convertToFloodlightPriorities(
        conversionWeightValues?.floodlightPriorities ?? []
      );

      let data: BiddingScriptGenerationData = {
        clientId,
        insertionOrderIds,
        startDate: startDate,
        endDate,
        conversionWeightData: {
          floodLightIds: conversionWeightValues?.floodlightIds,
          kValue: conversionWeightValues?.kValue ?? 0,
          isFloodlightPriority: conversionWeightValues?.isFloodlightPriority ?? false,
          floodlightPriorities: priorities,
        },
        platform,
      };

      if (domainScoringValues.isDomainScoringActivated) {
        data = {
          ...data,
          domainScoringData: {
            goal: domainScoringValues?.goal ?? '',
            maxCoef: domainScoringValues?.maxCoefficient ?? 0,
            aggressivity: domainScoringValues?.aggressivity ?? 0,
          },
        };
      }

      const script = await algorithmRepository.generateCustomBiddingAlgorithmScript(
        data,
        domainScoringValues.isDomainScoringActivated
      );

      if (script) {
        set({ previewScript: script });
      } else {
        set({
          snackBarProps: {
            messages: ['Generation returns an empty script'],
            severity: 'warning',
            visible: true,
          },
        });
        generationInSuccess = false;
      }
    } catch (e) {
      set({
        snackBarProps: {
          messages: ['Error generating conversion weight script'],
          severity: 'error',
          visible: true,
        },
      });
      generationInSuccess = false;
    } finally {
      set({ isGenerationLoading: false });
    }
    return generationInSuccess;
  },
  setFloodlightsPerPlatform: (cm360Floodlights: Floodlight[], dv360Floodlights: Floodlight[]) => {
    const cleanFloodlights = (floodlights: Floodlight[]): FloodlightValue[] => {
      return floodlights.reduce((acc: FloodlightValue[], floodlight) => {
        const existingItem = acc.find((i) => i.id === floodlight.id && i.name === floodlight.name);
        if (!existingItem) {
          acc.push({ id: floodlight.id, name: floodlight.name });
        }
        return acc;
      }, []);
    };
    const cm360 = cleanFloodlights(cm360Floodlights);
    const dv360 = cleanFloodlights(dv360Floodlights);
    set({
      floodlightsPerPlatform: { cm360, dv360 },
      floodlightAutoSelectValues: cm360,
    });
  },
  setFloodlightAutoSelectValues: (floodlights: Floodlight[]) => {
    const cleanedFloodlights = floodlights.reduce((acc: FloodlightValue[], floodlight) => {
      const existingItem = acc.find((i) => i.id === floodlight.id && i.name === floodlight.name);
      if (!existingItem) {
        acc.push({ id: floodlight.id, name: floodlight.name });
      }
      return acc;
    }, []);
    set({ floodlightAutoSelectValues: cleanedFloodlights });
  },
  setFloodlightIdsValues: (floodlightIdsValues: string[]) => {
    set({ floodlightIdsValues });
    set({ isFloodlightPriorityActive: !!floodlightIdsValues.length });
  },
  setFormDefaultValues: (formDefaultValues: ConversionWeightFormValues) =>
    set({ formDefaultValues }),
  resetValues: () =>
    set((state) => ({
      formDefaultValues: CONVERSION_WEIGHT_FORM_DEFAULT_VALUES,
      floodlightIdsValues: [],
      isFloodlightPriorityActive: false,
      selectedPlatform: 'CM360',
      floodlightAutoSelectValues: state.floodlightsPerPlatform.cm360,
    })),
  setSelectedPlatform: (selectedPlatform: FloodlightPlatform) =>
    set((state) => ({
      selectedPlatform,
      floodlightIdsValues: [],
      isFloodlightPriorityActive: false,
      floodlightAutoSelectValues:
        selectedPlatform === 'CM360'
          ? state.floodlightsPerPlatform.cm360
          : state.floodlightsPerPlatform.dv360,
    })),
}));
